import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Input, SparkIcon, TagColored } from '@spark';
import FieldLabel from '@/features/datasets/components/DataCollection/GenerateDataWithAI/shared/FieldLabel';
import { HELP_TEXTS } from '@/features/datasets/components/DataCollection/RetrievalAgent/constants';
import { formatFileSize } from '@/features/datasets/components/DataCollection/RetrievalAgent/providerOptions';

/**
 * "Exclude Datasets" field (Retrieval Agent Define Task step): enter-to-add
 * tag input plus an optional attached file whose names are merged in.
 */
const ExcludeDatasetsField = ({
    datasetsToExclude,
    onDatasetsToExcludeChange,
    excludeFile,
    onExcludeFileChange,
}) => {
    const [excludeDraft, setExcludeDraft] = useState('');
    const fileRef = useRef(null);

    const addExclude = (raw) => {
        const value = (raw || '').trim();
        if (!value) return;
        if (!datasetsToExclude.includes(value)) {
            onDatasetsToExcludeChange([...datasetsToExclude, value]);
        }
        setExcludeDraft('');
    };

    const handleExcludeFile = (event) => {
        const file = event.target.files?.[0];
        event.target.value = '';
        if (!file) return;
        onExcludeFileChange({ name: file.name, size: file.size });
        const reader = new FileReader();
        reader.onload = () => {
            const names = String(reader.result || '')
                .split(/[\n,]/)
                .map((part) => part.trim())
                .filter(Boolean);
            if (!names.length) return;
            onDatasetsToExcludeChange([...new Set([...datasetsToExclude, ...names])]);
        };
        reader.readAsText(file);
    };

    return (
        <div className="gdai-field">
            <FieldLabel text="Exclude Datasets" help={HELP_TEXTS.datasetsToExclude} />
            <Input
                className="gdai-exclude-input"
                showLabel={false}
                size="medium"
                placeholder="Add a dataset, source, or domain to avoid"
                helperText="Press ↵ Enter to add"
                showHelperText
                suffixIconSlug="attach"
                suffixIconAriaLabel="Attach a file with datasets to exclude"
                onSuffixIconClick={() => fileRef.current?.click()}
                value={excludeDraft}
                onChange={(event) => setExcludeDraft(event.target.value)}
                onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                        event.preventDefault();
                        addExclude(excludeDraft);
                    }
                }}
            />
            {datasetsToExclude.length ? (
                <div className="gdai-entity-tags">
                    {datasetsToExclude.map((name) => (
                        <TagColored
                            key={name}
                            label={name}
                            variant="blue"
                            showSuffixIcon
                            onDelete={() =>
                                onDatasetsToExcludeChange(
                                    datasetsToExclude.filter((item) => item !== name)
                                )
                            }
                        />
                    ))}
                </div>
            ) : null}
            <input
                ref={fileRef}
                type="file"
                hidden
                accept=".txt,.csv,.json"
                onChange={handleExcludeFile}
            />
            {excludeFile ? (
                <div className="gdai-file-card">
                    <SparkIcon slug="library" size="small" />
                    <div className="gdai-file-card__meta">
                        <div className="gdai-file-card__name fs-unmask">{excludeFile.name}</div>
                        <div className="gdai-file-card__size fs-unmask">
                            {formatFileSize(excludeFile.size)}
                        </div>
                    </div>
                    <button
                        type="button"
                        className="gdai-icon-button"
                        aria-label="Remove attached file"
                        onClick={() => onExcludeFileChange(null)}
                    >
                        <SparkIcon slug="trash" size="small" />
                    </button>
                </div>
            ) : null}
        </div>
    );
};

ExcludeDatasetsField.propTypes = {
    datasetsToExclude: PropTypes.arrayOf(PropTypes.string).isRequired,
    onDatasetsToExcludeChange: PropTypes.func.isRequired,
    excludeFile: PropTypes.shape({ name: PropTypes.string, size: PropTypes.number }),
    onExcludeFileChange: PropTypes.func.isRequired,
};

export default ExcludeDatasetsField;
